import { useEffect, useMemo } from 'react';

import { Button, Checkbox, Tooltip, Typography } from 'antd';
import { t } from 'i18next';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useClient } from 'src/api/axios/config';
import DrawerSelect from 'src/components/DrawerSelect';
import MobileDialog from 'src/components/MobileDialog';
import { FieldDescriptionText, FormItem, TabPageContentWrapper } from 'src/components/SharedDetailsComponents';
import { useGlobal } from 'src/contexts/GlobalContext';
import { useTheme } from 'src/contexts/ThemeContext';
import { useGetClient } from 'src/hooks/api/useClientsEndpoint';
import { useGetLists } from 'src/hooks/api/useListsEndpoint';
import { useDeleteCampaign } from 'src/hooks/api/useSiteEndpoint';
import { useGetVenues } from 'src/hooks/api/useVenuesEndpoint';
import { useModal } from 'src/hooks/useModal';
import SettingCard from 'src/pages/Devices/DeviceDetails/TabPages/SensorSettings/components/SettingCard';
import { useAppNavigation } from 'src/routes';

import { DeleteOutlined } from '@ant-design/icons';

import { useCampaignDetails } from '../../CampaignDetailsContext';
import { LabelWrapper, StyledInput, StyledSelect } from './Settings.styled';

const PROGRAM_OPTIONS = [
  {
    id: 'AccessControl',
    label: t('site.campaigns.campaign.settings.accessControl'),
    value: 'AccessControl',
  },
  {
    id: 'OrderFulfillment',
    label: t('site.campaigns.campaign.settings.pointOfSale'),
    value: 'OrderFulfillment',
  },
  { id: 'Ticketing', label: t('site.campaigns.campaign.settings.ticketing'), value: 'Ticketing' },
  {
    id: 'AgeVerification',
    label: t('site.campaigns.campaign.settings.identityVerification'),
    value: 'IdVerification',
  },
];

export const Settings = () => {
  const { isDesktop } = useGlobal();
  const client = useClient();
  const { t } = useTranslation();
  const { formMethods, campaign, site } = useCampaignDetails();
  const { confirm, contextHolder } = useModal();
  const { combinedTheme } = useTheme();
  const { mutateAsync: deleteCampaign } = useDeleteCampaign(site?.id ?? '');
  const { navigateTo } = useAppNavigation();
  const { data: clientData, isLoading: isClientLoading } = useGetClient(client ?? '');
  const { data: venueData, isLoading: isVenueLoading } = useGetVenues();
  const { data: listData, isLoading: isListLoading } = useGetLists();
  const { watch, setValue } = formMethods;
  const program = watch('program');
  const connectionId = watch('connectionId');
  const requireId = watch('requireId');

  const isSameConnectionType = (connectionType: string, connectionProgram: string) => {
    if (connectionType === 'Credentialing') {
      return connectionProgram === 'Credentialing' || connectionProgram === 'AccessControl';
    }
    if (connectionProgram === 'IdVerification') {
      return connectionType === 'AgeVerification';
    }
    return connectionType === connectionProgram;
  };

  const LIST_OPTIONS = listData?.map((list) => ({
    label: `${list.name}`,
    value: `${list.id}`,
  }));

  const VENUE_OPTIONS = venueData?.map((venue) => ({
    label: `${venue.name}`,
    value: `${venue.id}`,
  }));

  // Validate venue exists when venues data loads/changes
  useEffect(() => {
    if (campaign && venueData) {
      const currentVenueId = formMethods.getValues('venueId');
      const venueExists = venueData.some((venue) => venue.id === currentVenueId);

      if (!venueExists && currentVenueId) {
        formMethods.setValue('venueId', null);
      }
    }
  }, [venueData, campaign, formMethods]);

  const isTicketmasterOAuth =
    site?.authenticationConnection?.connectionType === 'Authentication' &&
    site?.authenticationConnection?.connectionTypeDetail === 'AuthenticationTicketmasterOAuth';

  const fortressAifiConnections = clientData?.connections?.filter(
    (connection) =>
      connection.connectionType === 'OrderFulfillment' &&
      connection.connectionTypeDetail === 'OrderFulfillmentFortressAiFi',
  );

  const CONNECTION_OPTIONS = useMemo(() => {
    const currentProgram = program || campaign?.program;

    if (!currentProgram) return [];

    return clientData?.connections
      ?.filter((connection) => {
        if (!connection.connectionType || !connection.id) return false;

        const programString = currentProgram;

        return isSameConnectionType(connection.connectionType, programString);
      })
      ?.map((connection) => {
        const connectionId = connection.id!;
        if (fortressAifiConnections?.some((fortressAifiConnection) => fortressAifiConnection.id === connectionId)) {
          return {
            label: (
              <Tooltip title={t('site.campaigns.campaign.settings.connectionDisabledTooltip')}>
                <LabelWrapper>{connection.name}</LabelWrapper>
              </Tooltip>
            ),
            value: connectionId,
            disabled: !isTicketmasterOAuth,
          };
        } else {
          return {
            label: `${connection.name}`,
            value: connectionId,
          };
        }
      });
  }, [clientData, campaign?.program, program, fortressAifiConnections, isTicketmasterOAuth, t]);

  useEffect(() => {
    const currentProgram = program || campaign?.program;
    if (!currentProgram || !connectionId || !clientData?.connections) return;

    const currentConnection = clientData.connections.find((conn) => conn.id === connectionId);
    if (!isSameConnectionType(currentConnection?.connectionType ?? '', currentProgram)) {
      setValue('connectionId', null);
    }
  }, [program, connectionId, clientData?.connections, campaign?.program, setValue]);

  const handleMutateRemoveCampaign = async () => {
    if (campaign?.id) {
      await deleteCampaign(
        { id: site?.id ?? '', campaignId: campaign.id },
        {
          onSuccess: () => navigateTo.siteDetails(site?.id ?? '', 'campaigns'),
        },
      );
    }
  };

  const handleDeleteCampaign = () => {
    if (isDesktop) {
      confirm({
        title: `${t('site.campaigns.campaign.settings.deleteCampaign')}?`,
        onOk: async () => await handleMutateRemoveCampaign(),
      });
    } else {
      return MobileDialog({
        title: `${t('site.campaigns.campaign.settings.deleteCampaign')}?`,
        description: t('general.cannotBeUndone'),
        confirmText: t('site.campaigns.campaign.settings.deleteCampaignConfirmText'),
        cancelText: t('site.campaigns.campaign.settings.deleteCampaignCancelText'),
        onConfirm: async () => handleMutateRemoveCampaign(),
        theme: combinedTheme,
      });
    }
  };

  return (
    <>
      <SettingCard
        title={t('site.campaigns.campaign.settings.general')}
        description={t('site.campaigns.campaign.settings.description')}
        items={[
          {
            content: (
              <TabPageContentWrapper $isDesktop={isDesktop} data-testid="campaign-settings-form">
                <FormItem
                  $isDesktop={isDesktop}
                  label={t('site.campaigns.campaign.settings.campaignPath')}
                  name="pathSegment"
                  help={formMethods?.formState?.errors?.pathSegment?.message}
                  validateStatus={formMethods?.formState?.errors?.pathSegment ? 'error' : ''}
                  required
                >
                  <Controller
                    name="pathSegment"
                    control={formMethods.control}
                    render={({ field }) => (
                      <StyledInput
                        {...field}
                        prefix={
                          <Typography.Text>{`/${site?.pathSegment?.length ? site?.pathSegment : '[site-slug]'}/`}</Typography.Text>
                        }
                        name="pathSegment"
                        placeholder={t('site.campaigns.campaign.settings.campaignPathPlaceholder')}
                        data-testid="campaign-path-input"
                      />
                    )}
                  />
                </FormItem>
                <FormItem $isDesktop={isDesktop} label={t('site.campaigns.campaign.settings.tagline')} name="tagline">
                  <Controller
                    name="tagline"
                    control={formMethods.control}
                    render={({ field }) => (
                      <>
                        <StyledInput
                          {...field}
                          name="tagline"
                          placeholder={t('site.campaigns.campaign.settings.tagline')}
                          data-testid="campaign-tagline-input"
                        />
                        <FieldDescriptionText>
                          {t('site.campaigns.campaign.settings.taglineDescription')}
                        </FieldDescriptionText>
                      </>
                    )}
                  />
                </FormItem>
              </TabPageContentWrapper>
            ),
          },
        ]}
      />
      <SettingCard
        title={t('site.campaigns.campaign.settings.connections.title')}
        description={t('site.campaigns.campaign.settings.connections.description')}
        items={[
          {
            content: (
              <TabPageContentWrapper $isDesktop={isDesktop} data-testid="campaign-connections-form">
                <FormItem
                  $isDesktop={isDesktop}
                  label={t('site.campaigns.campaign.settings.program')}
                  name="program"
                  help={formMethods?.formState?.errors?.program?.message}
                  validateStatus={formMethods?.formState?.errors?.program ? 'error' : ''}
                  required
                >
                  <Controller
                    name="program"
                    control={formMethods.control}
                    render={({ field }) => {
                      if (isDesktop) {
                        return (
                          <StyledSelect
                            {...field}
                            placeholder={t('site.campaigns.campaign.settings.programPlaceholder')}
                            options={PROGRAM_OPTIONS ?? []}
                            disabled={!!campaign?.program?.length}
                            data-testid="campaign-program-select"
                          />
                        );
                      } else {
                        return (
                          <DrawerSelect
                            {...field}
                            options={PROGRAM_OPTIONS ?? []}
                            onOk={field.onChange}
                            title={t('site.campaigns.campaign.settings.program')}
                            placeholder={t('site.campaigns.campaign.settings.programPlaceholder')}
                            onOkText={t('site.campaigns.campaign.settings.save')}
                            disabled={!!campaign?.program?.length}
                            data-testid="campaign-program-select"
                          />
                        );
                      }
                    }}
                  />
                </FormItem>
                <FormItem
                  $isDesktop={isDesktop}
                  label={t('site.campaigns.campaign.settings.connection')}
                  name="connectionId"
                >
                  <Controller
                    name="connectionId"
                    control={formMethods.control}
                    render={({ field }) => {
                      if (isDesktop) {
                        return (
                          <StyledSelect
                            {...field}
                            value={isClientLoading ? null : field.value}
                            placeholder={t('site.campaigns.campaign.settings.programConnectionPlaceholder')}
                            options={CONNECTION_OPTIONS ?? []}
                            data-testid="campaign-connection-select"
                            loading={isClientLoading}
                          />
                        );
                      } else {
                        return (
                          <DrawerSelect
                            {...field}
                            value={isClientLoading ? null : field.value}
                            options={CONNECTION_OPTIONS ?? []}
                            onOk={field.onChange}
                            title={t('site.campaigns.campaign.settings.connection')}
                            placeholder={t('site.campaigns.campaign.settings.programConnectionPlaceholder')}
                            onOkText={t('site.campaigns.campaign.settings.save')}
                            data-testid="campaign-connection-select"
                            loading={isClientLoading}
                          />
                        );
                      }
                    }}
                  />
                </FormItem>
                {program === 'OrderFulfillment' && (
                  <>
                    <FormItem $isDesktop={!requireId ? false : isDesktop} name="requireId">
                      <Controller
                        name="requireId"
                        control={formMethods.control}
                        render={({ field }) => (
                          <Checkbox
                            {...field}
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            data-testid="campaign-require-id-checkbox"
                          >
                            {t('site.campaigns.campaign.settings.alsoRequireId')}
                          </Checkbox>
                        )}
                      />
                    </FormItem>
                    {requireId && (
                      <FormItem
                        $isDesktop={isDesktop}
                        name="requiredMinimumAge"
                        label={t('site.campaigns.campaign.settings.requiredMinimumAge')}
                      >
                        <Controller
                          name="requiredMinimumAge"
                          control={formMethods.control}
                          render={({ field }) => (
                            <>
                              <StyledInput
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value || null)}
                                type="number"
                                placeholder={t('site.campaigns.campaign.settings.requiredMinimumAgePlaceholder')}
                                data-testid="campaign-required-minimum-age-input"
                              />
                              <FieldDescriptionText>
                                {t('site.campaigns.campaign.settings.requiredMinimumAgeDescription')}
                              </FieldDescriptionText>
                            </>
                          )}
                        />
                      </FormItem>
                    )}
                  </>
                )}
                <FormItem $isDesktop={isDesktop} label={t('site.campaigns.campaign.settings.venue')} name="venueId">
                  <Controller
                    name="venueId"
                    control={formMethods.control}
                    render={({ field }) => {
                      if (isDesktop) {
                        return (
                          <StyledSelect
                            {...field}
                            value={isVenueLoading ? null : field.value}
                            placeholder={t('site.campaigns.campaign.settings.venuePlaceholder')}
                            options={VENUE_OPTIONS ?? []}
                            data-testid="campaign-venue-select"
                            loading={isVenueLoading}
                          />
                        );
                      } else {
                        return (
                          <DrawerSelect
                            {...field}
                            value={isVenueLoading ? null : field.value}
                            options={VENUE_OPTIONS ?? []}
                            onOk={field.onChange}
                            title={t('site.campaigns.campaign.settings.venue')}
                            placeholder={t('site.campaigns.campaign.settings.venuePlaceholder')}
                            onOkText={t('site.campaigns.campaign.settings.save')}
                            data-testid="campaign-venue-select"
                            loading={isVenueLoading}
                          />
                        );
                      }
                    }}
                  />
                </FormItem>
                <FormItem
                  $isDesktop={isDesktop}
                  label={t('site.campaigns.campaign.settings.list')}
                  name="listId"
                  help={formMethods?.formState?.errors?.listId?.message}
                  validateStatus={formMethods?.formState?.errors?.listId ? 'error' : ''}
                  required
                >
                  <Controller
                    name="listId"
                    control={formMethods.control}
                    render={({ field }) => {
                      if (isDesktop) {
                        return (
                          <StyledSelect
                            {...field}
                            value={isListLoading ? null : field.value}
                            placeholder={t('site.campaigns.campaign.settings.listPlaceholder')}
                            options={LIST_OPTIONS ?? []}
                            data-testid="campaign-list-select"
                            loading={isListLoading}
                          />
                        );
                      } else {
                        return (
                          <DrawerSelect
                            {...field}
                            value={isListLoading ? null : field.value}
                            options={LIST_OPTIONS ?? []}
                            onOk={field.onChange}
                            title={t('site.campaigns.campaign.settings.list')}
                            placeholder={t('site.campaigns.campaign.settings.listPlaceholder')}
                            onOkText={t('site.campaigns.campaign.settings.save')}
                            data-testid="campaign-list-select"
                            loading={isListLoading}
                          />
                        );
                      }
                    }}
                  />
                </FormItem>
              </TabPageContentWrapper>
            ),
          },
        ]}
      />
      {contextHolder}
      <SettingCard
        title={t('site.campaigns.campaign.settings.deleteCampaign')}
        description={t('site.campaigns.campaign.settings.deleteCampaignDescription', { name: campaign?.name })}
        items={[
          {
            action: (
              <Button
                disabled={!campaign?.id}
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteCampaign}
                block={!isDesktop}
                data-testid="campaign-delete-campaign-button"
              >
                {t('site.campaigns.campaign.settings.deleteCampaign')}
              </Button>
            ),
          },
        ]}
      />
    </>
  );
};
